<script setup>
import Tooltip from '@/components/common/base/Tooltip';
import PFBaseModal from "@/components/common/modal/PFBaseModal.vue";
import { useI18n } from 'vue-i18n';
import { ref, computed, watch, onMounted } from 'vue';
import "@sapudex/web-components/dist/Checkbox.js";
import "@sapudex/web-components/dist/TextArea.js";

const MAX_LENGTH_BRAND_NAME = 90;
const MAX_LENGTH_SHORT_DESCRIPTION = 300;

const props = defineProps({
  brandNameProp: { type: Object, default: () => ({ idx: null, brandName: {} }) },
  addAction: { type: Function, default: () => false },
  submitAction: { type: Function, default: () => false },
  cancelAction: { type: Function, default: () => false },
});

const { t, tc } = useI18n();
const emptyDescription = ref(false);
const errorBrandName = ref(false);
const emptyBrandName = ref(false);
const updatedBrandNameIdx = ref('');
const updatedBrandName = ref('');
const updatedShortDescription = ref('');
const addMode = ref(true);
const brandNameTimer = ref(null);
const shortDescriptionTimer = ref(null);
const brandNameMaxLength = ref(MAX_LENGTH_BRAND_NAME);
const shortDescriptionMaxLength = ref(MAX_LENGTH_SHORT_DESCRIPTION);
const brandNameCheckBox = ref(false);
const maxBrandHeaderChar = ref(50);

function onCheckboxChange(event) {
  brandNameCheckBox.value = event.target.checked;
};

function updateBrandName(event) {
  updatedBrandName.value = event.target.value;
  if (brandNameTimer.value) {
    clearTimeout(brandNameTimer.value);
  }
  brandNameTimer.value = setTimeout(() => checkUpdatedBrandName(), 2000);
};

function updateShortDescription(event) {
  updatedShortDescription.value = event.target.value;
  if (shortDescriptionTimer.value) {
    clearTimeout(shortDescriptionTimer.value);
  }
  shortDescriptionTimer.value = setTimeout(() => checkUpdatedShortDescription(), 2000);
};

function submitUpdatedBrandName() {
  checkUpdatedBrandName();
  checkUpdatedShortDescription();
  if (!disableSave.value) {
    const updatedData = buildUpdatedData();
    addMode.value ? props.addAction(updatedData) 
      : props.submitAction(updatedData, updatedBrandNameIdx.value);
  }
};

function checkUpdatedBrandName() {
  emptyBrandName.value = updatedBrandName.value.length <= 0;
  const re = /^[A-Za-z0-9]/;
  errorBrandName.value = updatedBrandName.value.length > 0 && !re.test(updatedBrandName.value);
};

function checkUpdatedShortDescription() {
  emptyDescription.value = updatedShortDescription.value.length <= 0;
};

function buildUpdatedData() {
  const updatedData = {
    brandName: updatedBrandName.value,
    shortDescription: updatedShortDescription.value,
    isFavoriteName: brandNameCheckBox.value,
  };
  if (props.brandNameProp.brandName?.id) {
    updatedData.id = props.brandNameProp.brandName.id;
  }
  return updatedData;
};

function getTruncatedText(title) {
  let maxChar = maxBrandHeaderChar.value;
  maxChar = maxChar < 5 ? 5 : maxChar;
  return title.length <= maxChar ? title : `${title.substring(0, maxChar - 2)}...`;
};

const disableSave = computed(() => emptyBrandName.value || errorBrandName.value || emptyDescription.value);

const shortDescriptionLeft = computed(() => shortDescriptionMaxLength.value - updatedShortDescription.value.length);

const modalHeader = computed(() => {
  return addMode.value
    ? t("editor.brand.add.name.modal.header") 
    : `${t("button.edit")} "${getTruncatedText(props.brandNameProp.brandName.brandName)}"`;
});

const supportingText = computed(() => {
    return `${shortDescriptionLeft.value} ${t('editor.global.characters.left', shortDescriptionLeft.value)}`;
});
  
onMounted(() => {
  updatedBrandNameIdx.value = props.brandNameProp.idx;
  updatedBrandName.value = props.brandNameProp.brandName?.brandName || '';
  updatedShortDescription.value = props.brandNameProp.brandName?.shortDescription || '';
  addMode.value = updatedBrandNameIdx.value === null;
  brandNameCheckBox.value = props.brandNameProp.brandName?.isFavoriteName || false;
});
</script>

<template>
  <PFBaseModal :modalHeaderText="modalHeader" modalControlKey="addBrandNameModal" @keyup.esc="cancelAction">
    <template #default>
      <div>
        <udex-text-field
          :label="$t('editor.brand.name')"
          :value="updatedBrandName"
          @input="updateBrandName"
          @blur="checkUpdatedBrandName"
          required
          :maxlength="brandNameMaxLength" 
          accessibleName="Brand Name"     
        />
        <div class="h-5"/>
        <udex-textarea
          class="text-area"
          :label="$t('editor.brand.name.short.description')"
          :value="updatedShortDescription"
          @input="updateShortDescription"
          @blur="checkUpdatedShortDescription"
          :maxlength="shortDescriptionMaxLength"
          required
          :supporting-text="supportingText"
          accessibleName="Brand Name Description"
        />     
        <div class="h-5"/>
        <div class="brand-name-check-box-container">
          <udex-checkbox 
            class="-ml-2" 
            :text="$t('editor.brand.favorite.name')" 
            :checked="brandNameCheckBox"
            @change="onCheckboxChange" 
            accessibleName="Is Favorite Name Checkbox"
          />
          <span class="-ml-2">
            <Tooltip>
              <template #text>{{$t('editor.brand.favorite.name.help')}}</template>
            </Tooltip>
          </span>
        </div>
        <div class="edit-brand-name-text">
          {{ $t("editor.brand.name.edit.text") }}
        </div>
      </div>
    </template>

    <template #buttons>
      <udex-button
        @click="submitUpdatedBrandName"
        :disabled="disableSave"
        design="Primary"
        size="Large"
        slot="endContent"
        :accessibleName="addMode? 'Add Brand Name' : 'Save Brand Name'"
      >
        {{ addMode ? $t("button.add") : $t("button.save")}}
      </udex-button>
      <udex-button
        @click="cancelAction"
        design="Secondary"
        size="Large"
        slot="endContent"
        accessibleName="Cancel Adding Brand Name"
      >
        {{ $t("button.cancel") }}
      </udex-button>
    </template>
  </PFBaseModal>
</template>

<style lang="less" scoped>
#modal .text-area {
  --udex-textarea-height: 128px;
}
.edit-brand-name-text {
  &:extend(.body-xs-size);
  color: @udexCoreTextDark;
  font-style: normal;
}
.brand-name-check-box-container {
  &:extend(.body-s-size);
  display: flex;
  align-items: center;
  font-style: normal;
}
@media (max-width: 638px) {
  #modal .text-area {
  --udex-textarea-height: 256px;
}
}
</style>
